/* eslint-disable @typescript-eslint/no-unused-vars */
import Snacker from "@command-center-app/components/Snackbar";
import appRoutes from "@command-center-app/constants/app.routes.const";
import { GenericTableProps } from "@command-center-app/index";
import { colorPalette } from "@command-center-app/utils/colorPalette";
import { getStatusBadgeStyle } from "@command-center-app/utils/statusBadgeStyles";
import { capitalizeFirstLetter } from "@command-center-app/utils/common.util";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import FilterListIcon from "@mui/icons-material/FilterList";
import InfoIcon from "@mui/icons-material/Info";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import UnfoldMoreOutlinedIcon from "@mui/icons-material/UnfoldMoreOutlined";
import {
  CircularProgress,
  Grid,
  MenuItem,
  Select,
  Tooltip,
} from "@mui/material";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import { visuallyHidden } from "@mui/utils";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import ActionPopover from "./GenericTablePopover";
import { columnNames } from "@command-center-app/helpers/data.tsx";
import { tableStyle } from "./styles/commonStyles";
import GenericSearch from "./GenericSearch";

/**
 * Inline editable sequence cell for the optional `#` column.
 *
 * Behavior (matches the agreed UX for collection product reordering):
 * - The input always shows the row's current `sequence`.
 * - On Enter or blur the value is parsed and validated.
 * - Valid + changed values fire `onCommit`. The parent owns the
 *   mutation, optimistic update, and rollback so this cell stays
 *   reusable for any sequence-driven listing.
 * - Invalid values (empty, non-integer, out of [1..upperBound]) are
 *   rejected: an inline error renders, the mutation is NOT called,
 *   and on blur the input reverts to the row's current sequence so
 *   no stale input value is left behind.
 * - Esc reverts and blurs, like a typical inline editor.
 */
function SequenceEditorCell({
  currentSequence,
  upperBound,
  onCommit,
  ariaLabel,
}: {
  currentSequence: number | null | undefined;
  upperBound: number;
  onCommit: (newSequence: number) => void | Promise<void>;
  ariaLabel: string;
}) {
  const initial =
    typeof currentSequence === "number" && Number.isFinite(currentSequence)
      ? String(currentSequence)
      : "";
  const [draft, setDraft] = useState<string>(initial);
  const [error, setError] = useState<string | null>(null);

  // Keep the input in sync when the parent re-renders with a new
  // server-confirmed sequence (after a successful reorder).
  useEffect(() => {
    setDraft(initial);
    setError(null);
  }, [initial]);

  const safeUpperBound = Math.max(upperBound, 1);

  const validate = (raw: string): number | null => {
    if (raw.trim() === "") return null;
    if (!/^\d+$/.test(raw.trim())) return null;
    const parsed = Number(raw);
    if (!Number.isInteger(parsed)) return null;
    if (parsed < 1 || parsed > safeUpperBound) return null;
    return parsed;
  };

  const tryCommit = async () => {
    const parsed = validate(draft);
    if (parsed === null) {
      setError(`Enter a whole number between 1 and ${safeUpperBound}`);
      return false;
    }
    setError(null);
    if (parsed === currentSequence) return true;
    await onCommit(parsed);
    return true;
  };

  return (
    <TableCell
      padding="none"
      align="center"
      sx={{ width: "56px" }}
      onClick={(e) => e.stopPropagation()}
    >
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
        <input
          type="number"
          min={1}
          max={safeUpperBound}
          step={1}
          value={draft}
          aria-label={ariaLabel}
          aria-invalid={!!error}
          onChange={(e) => setDraft(e.target.value)}
          onMouseDown={(e) => e.stopPropagation()}
          onKeyDown={async (e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              await tryCommit();
            } else if (e.key === "Escape") {
              e.preventDefault();
              setDraft(initial);
              setError(null);
              (e.currentTarget as HTMLInputElement).blur();
            }
          }}
          onBlur={async () => {
            const committed = await tryCommit();
            if (!committed) {
              // Reject + revert: rollback the displayed value to the
              // last known good sequence so the UI never lingers on an
              // invalid number after the user moves away.
              setDraft(initial);
              setError(null);
            }
          }}
          style={{
            width: "44px",
            padding: "4px 6px",
            border: `1px solid ${
              error ? "#d32f2f" : colorPalette.colorLightGray
            }`,
            borderRadius: "4px",
            textAlign: "center",
            fontSize: "13px",
          }}
        />
        {error && (
          <Typography
            sx={{
              color: "#d32f2f",
              fontSize: "10px",
              marginTop: "2px",
              lineHeight: 1.2,
              textAlign: "center",
              maxWidth: "120px",
            }}
          >
            {error}
          </Typography>
        )}
      </Box>
    </TableCell>
  );
}

function getResizableColumnSx(
  headCell: any,
  width: number,
  options: { isHeader?: boolean } = {}
) {
  const base = {
    width,
    minWidth: width,
    maxWidth: width,
    ...(options.isHeader ? { position: "relative" as const } : {}),
  };

  if (headCell.wrapText) {
    return {
      ...base,
      overflow: "hidden",
      whiteSpace: "normal" as const,
      wordBreak: "break-word" as const,
      overflowWrap: "anywhere" as const,
      verticalAlign: "top" as const,
    };
  }

  return {
    ...base,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap" as const,
  };
}

function getHeaderColumnSx(
  headCell: any,
  columnWidths: Record<string, number>,
  resizableColumns: boolean
) {
  if (resizableColumns && columnWidths[headCell.id] != null) {
    return getResizableColumnSx(headCell, columnWidths[headCell.id], {
      isHeader: true,
    });
  }
  if (
    headCell.id === "id" ||
    headCell.id === "product" ||
    headCell.id === "service" ||
    headCell.id === "tags" ||
    headCell.id === "collectionName"
  ) {
    return tableStyle.bigColumn;
  }
  if (headCell.id) {
    return tableStyle.smallColumn;
  }
  return { width: "18px" };
}

function EnhancedTableHead({
  headers,
  onSelectAllClick,
  order,
  orderBy,
  numSelected,
  rowCount,
  onRequestSort,
  setSorted,
  enableDragAndDrop = false,
  enableManualSequence = false,
  resizableColumns = false,
  columnWidths = {},
  onColumnResizeStart,
}: {
  headers: any;
  onSelectAllClick: any;
  order: any;
  orderBy: string;
  numSelected: number;
  rowCount: number;
  onRequestSort: any;
  setSorted: any;
  enableDragAndDrop?: boolean;
  enableManualSequence?: boolean;
  resizableColumns?: boolean;
  columnWidths?: Record<string, number>;
  onColumnResizeStart?: (headerId: string, event: React.MouseEvent) => void;
}) {
  const createSortHandler = (property: any) => (event: any) => {
    onRequestSort(event, property);
    setSorted([{ order: order, fieldName: property }]);
  };

  return (
    <TableHead sx={{ display: "table", width: "100%", tableLayout: "fixed" }}>
      <TableRow
        sx={{
          backgroundColor: colorPalette.colorVeryLightGray,
          border: `1px solid ${colorPalette.colorSoftLightGray}`,
        }}
      >
        {enableDragAndDrop && (
          <TableCell padding="checkbox" sx={{ width: "18px" }}>
            {/* Empty header for drag handle column */}
          </TableCell>
        )}
        {enableManualSequence && (
          <TableCell
            padding="none"
            align="center"
            sx={{ width: "56px", fontWeight: 500 }}
          >
            #
          </TableCell>
        )}
        <TableCell padding="checkbox" sx={{ width: "18px" }}>
          <Checkbox
            color="primary"
            indeterminate={numSelected > 0 && numSelected < rowCount}
            checked={rowCount > 0 && numSelected === rowCount}
            onChange={onSelectAllClick}
            inputProps={{
              "aria-label": "select all desserts",
            }}
          />
        </TableCell>
        {headers.map((headCell: any) => (
          <TableCell
            key={headCell.id}
            align={
              headCell.centerAlign
                ? "center"
                : headCell.numeric
                ? "right"
                : "left"
            }
            padding={headCell.disablePadding ? "none" : "normal"}
            sortDirection={orderBy === headCell.id ? order : false}
            sx={getHeaderColumnSx(headCell, columnWidths, resizableColumns)}
          >
            {headCell?.tooltip ? (
              <Tooltip title={headCell.tooltipMsg} placement="top">
                <TableSortLabel
                  active={orderBy === headCell.id}
                  direction={orderBy === headCell.id ? order : "asc"}
                  onClick={createSortHandler(headCell.id)}
                  IconComponent={UnfoldMoreOutlinedIcon}
                >
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    {headCell.label}
                    <IconButton size="small">
                      <InfoIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  {orderBy === headCell.id ? (
                    <Box component="span" sx={visuallyHidden}>
                      {order === "desc"
                        ? "sorted descending"
                        : "sorted ascending"}
                    </Box>
                  ) : null}
                </TableSortLabel>
              </Tooltip>
            ) : (
              <TableSortLabel
                active={orderBy === headCell.id}
                direction={orderBy === headCell.id ? order : "asc"}
                onClick={createSortHandler(headCell.id)}
                IconComponent={UnfoldMoreOutlinedIcon}
              >
                {headCell.label}
                {orderBy === headCell.id ? (
                  <Box component="span" sx={visuallyHidden}>
                    {order === "desc"
                      ? "sorted descending"
                      : "sorted ascending"}
                  </Box>
                ) : null}
              </TableSortLabel>
            )}
            {resizableColumns && (
              <Box
                role="separator"
                aria-orientation="vertical"
                aria-label={`Resize ${headCell.label} column`}
                onMouseDown={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  onColumnResizeStart?.(headCell.id, event);
                }}
                onClick={(event) => event.stopPropagation()}
                sx={{
                  position: "absolute",
                  top: 0,
                  right: 0,
                  width: 6,
                  height: "100%",
                  cursor: "col-resize",
                  zIndex: 2,
                  "&:hover": {
                    backgroundColor: "rgba(0, 0, 0, 0.08)",
                  },
                }}
              />
            )}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
function EnhancedTableToolbar(_props: {
  listCount?: number | undefined;
  query?: any;
  setQuery?: any;
  customizeQuery?: any;
  applyFilter?: boolean;
  setApplyFilter?: any;
  totalRows?: number;
  rowsPerPage?: number;
  setSearchText?: any;
  filterDataTypes?: any;
  setController?: any;
  numSelected?: number;
}) {
  return (
    <>
      <Toolbar
        sx={{
          pl: { sm: 2 },
          pr: { xs: 1, sm: 1 },
        }}
        style={{ padding: "0px", width: "20%" }}
      >
        <>
          <IconButton onClick={() => {}}>
            <SearchOutlinedIcon />
          </IconButton>
          <Typography
            sx={{ flex: "1 1 12%", cursor: "pointer" }}
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            variant="p"
            id="tableTitle"
            component="div"
            fontFamily="Inter"
            fontSize="14px"
            onClick={() => {}}
          >
            Search
          </Typography>
          <IconButton aria-describedby={"filter"} onClick={(_e: any) => {}}>
            <FilterListIcon />
          </IconButton>
          <Typography
            sx={{ flex: "1 1 12%", cursor: "pointer" }}
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            variant="p"
            id="tableTitle"
            component="div"
            fontFamily="Inter"
            fontSize="14px"
            onClick={() => {}}
          >
            Filter
          </Typography>
        </>
      </Toolbar>
    </>
  );
}

function GenericTable({
  headers,
  rows = [],
  actions,
  page,
  rowsPerPage,
  onPageChange,
  totalCount,
  loading,
  hasSearch = false,
  hasFilter = false,
  hasClear = true,
  onSearchTextChange,
  onSortChange,
  onStatusFilterChange,
  onTypeFilterChange,
  onClearAllFilters,
  setSelected,
  selected,
  openSnackerToast,
  setOpenSnackerToast,
  snackbarActions,
  showSnackbar = false,
  enableDragAndDrop = false,
  preserveRowOrder = false,
  enableManualSequence = false,
  onSequenceChange,
  sequenceUpperBound,
  resetFiltersSignal,
  onRowsReorder,
  filterSlot,
  resizableColumns = false,
}: GenericTableProps) {
  const [popoverRow, setPopoverRow] = useState<any>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [order, setOrder] = useState<string>("asc");
  const [orderBy, setOrderBy] = useState<string>("product");
  const [_sorted, setSorted] = useState<any[]>([
    {
      order: order,
      fieldName: orderBy,
    },
  ]);
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState<string>("");
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [sortedRows, setSortedRows] = useState<any[]>([]);
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>({});
  const snackbarText = "item";

  const visibleHeaders =
    (actions?.length ?? 0) > 0
      ? headers
      : headers.filter((h: any) => h.id !== "action");

  const headerWidthKey = (visibleHeaders || [])
    .map((header: any) => `${header.id}:${header.width ?? 140}`)
    .join("|");

  useEffect(() => {
    if (!resizableColumns || !visibleHeaders?.length) return;
    setColumnWidths((prev) => {
      const next = { ...prev };
      let changed = false;
      visibleHeaders.forEach((header: any) => {
        if (next[header.id] == null) {
          next[header.id] = typeof header.width === "number" ? header.width : 140;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [resizableColumns, headerWidthKey]);

  const handleColumnResizeStart = (
    headerId: string,
    event: React.MouseEvent
  ) => {
    const startX = event.clientX;
    const startWidth = columnWidths[headerId] ?? 140;

    const onMouseMove = (moveEvent: MouseEvent) => {
      const nextWidth = Math.max(80, startWidth + (moveEvent.clientX - startX));
      setColumnWidths((prev) => ({ ...prev, [headerId]: nextWidth }));
    };

    const onMouseUp = () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };

    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  };

  const getBodyColumnSx = (header: any) => {
    if (resizableColumns && columnWidths[header.id] != null) {
      return getResizableColumnSx(header, columnWidths[header.id]);
    }
    if (header.wrapText) {
      return {
        ...(typeof header.width === "number"
          ? {
              width: header.width,
              minWidth: header.width,
              maxWidth: header.width,
            }
          : {}),
        overflow: "hidden",
        whiteSpace: "normal" as const,
        wordBreak: "break-word" as const,
        overflowWrap: "anywhere" as const,
        verticalAlign: "top" as const,
      };
    }
    if (
      header.id === "id" ||
      header.id === "product" ||
      header.id === "service" ||
      header.id === "tags" ||
      header.id === "collectionName"
    ) {
      return tableStyle.bigColumn;
    }
    if (columnNames.includes(header.id)) {
      return tableStyle.smallColumn;
    }
    return { width: "10px" };
  };

  const tableMinWidth = resizableColumns
    ? Object.values(columnWidths).reduce((sum, width) => sum + width, 60)
    : 750;

  const activeFilters = hasFilter
    ? Array.isArray(hasFilter)
      ? hasFilter
      : ["status", "type"]
    : [];

  useEffect(() => {
    console.log(resetFiltersSignal)
    handleClearFilters();
  }, [resetFiltersSignal]);

  useEffect(() => {
    if (onSortChange) {
      // Parent controls sorting via API. Keep incoming page order.
      setSortedRows(rows);
    }
    // Opt-in escape hatch for callers whose data is already sorted
    // server-side (e.g. by a per-row `sequence`). Without this, the
    // built-in sort by `product.text` ASC would shuffle merchandising
    // order whenever rows reach the table. Defaults to false so every
    // other consumer keeps its existing behaviour.
    if (preserveRowOrder) {
      setSortedRows([...rows]);
      return;
    }

    const sorted = [...rows]?.sort((a, b) => {
      let aValue = a[orderBy];
      let bValue = b[orderBy];

      if (orderBy === "product") {
        aValue = aValue?.text || "";
        bValue = bValue?.text || "";
      } else if (orderBy === "vendor") {
        aValue = aValue?.name || "";
        bValue = bValue?.name || "";
      }

      if (typeof aValue === "string" && typeof bValue === "string") {
        return order === "asc"
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      if (typeof aValue === "number" && typeof bValue === "number") {
        return order === "asc" ? aValue - bValue : bValue - aValue;
      }

      return 0;
    });
    setSortedRows(sorted);
  }, [rows, order, orderBy, onSortChange,preserveRowOrder]);

  // Handler to clear all filters and search
  const handleClearFilters = () => {
    console.log("Clearing filters");
    setSearchText("");
    setStatusFilter("");
    setTypeFilter("");
    onClearAllFilters?.();
  };

  const handleStatusFilterChange = (event: any) => {
    setStatusFilter(event.target.value as string);
    if (onStatusFilterChange) {
      onStatusFilterChange(event.target.value as string);
    }
  };

  const handleTypeFilterChange = (event: any) => {
    setTypeFilter(event.target.value as string);
    if (onTypeFilterChange) {
      onTypeFilterChange(event.target.value as string);
    }
  };

  const isSelected = (name: string) => selected.indexOf(name) !== -1;

  const handleRequestSort = (
    _event: React.MouseEvent<unknown>,
    property: string
  ) => {
    const isAsc = orderBy === property && order === "asc";
    const nextOrder = isAsc ? "desc" : "asc";
    setOrder(nextOrder);
    setOrderBy(property);
    onSortChange?.(property, nextOrder);
  };

  const handleClick = (_event: React.MouseEvent<unknown>, id: string) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected: string[] = [];

    if (selectedIndex === -1) {
      newSelected = [...selected, id];
    } else if (selectedIndex === 0) {
      newSelected = selected.slice(1);
    } else if (selectedIndex === selected.length - 1) {
      newSelected = selected.slice(0, -1);
    } else if (selectedIndex > 0) {
      newSelected = [
        ...selected.slice(0, selectedIndex),
        ...selected.slice(selectedIndex + 1),
      ];
    }

    setSelected(newSelected);
    setOpenSnackerToast(false);
    setTimeout(() => setOpenSnackerToast(true), 0);
  };

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelected = currentRows.map((n) => n.id);
      setSelected(newSelected);
    } else {
      setSelected([]);
    }
    setOpenSnackerToast(false);
    setTimeout(() => setOpenSnackerToast(true), 0);
  };

  const handleDragStart = (
    event: React.DragEvent<HTMLTableRowElement>,
    index: number
  ) => {
    setDraggedIndex(index);
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData("text/html", "");
  };

  const handleDragOver = (event: React.DragEvent<HTMLTableRowElement>) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
  };

  const handleDrop = (
    event: React.DragEvent<HTMLTableRowElement>,
    dropIndex: number
  ) => {
    event.preventDefault();

    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      return;
    }

    const newRows = [...sortedRows];
    const draggedRow = newRows[draggedIndex];
    newRows.splice(draggedIndex, 1);
    newRows.splice(dropIndex, 0, draggedRow);
    setSortedRows(newRows);
    setDraggedIndex(null);

    // Notify parent component of the reorder
    if (onRowsReorder) {
      const itemId = sortedRows[draggedIndex].id;
      onRowsReorder(newRows, itemId, dropIndex);
    }
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  const currentRows = sortedRows.slice(0, rowsPerPage || sortedRows.length);

  const handlePopoverOpen = (
    event: React.MouseEvent<HTMLElement>,
    row: any
  ) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setPopoverRow(row);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
    setPopoverRow(null);
  };

  const handleActionClick = (action: string, rowData: any) => {
    console.log(`Action: ${action}`, rowData);
  };

  return (
    <Grid
      id="table-grid"
      container
      paddingBottom="24px"
      style={{ display: "inline" }}
    >
      <Box sx={{ width: "100%" }}>
        <Paper sx={{ width: "100%", mb: 2, boxShadow: "none" }}>
          <Box gap={2} sx={{ display: "flex" }}>
            {filterSlot && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
                {filterSlot}
              </Box>
            )}
            {hasSearch && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
                <GenericSearch
                  value={searchText}
                  placeholder="View all"
                  onSearch={(value) => {
                    setSearchText(value);
                    onSearchTextChange?.(value);
                  }}
                />
              </Box>
            )}
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
              {/* Status Filter */}
              {activeFilters.includes("status") && (
                <Select
                  value={statusFilter}
                  onChange={handleStatusFilterChange}
                  displayEmpty
                  size="small"
                  sx={{ minWidth: 120, background: "#fff" }}
                >
                  <MenuItem value="">All Status</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              )}
              {/* Type Filter */}
              {activeFilters.includes("type") && (
                <Select
                  value={typeFilter || ""}
                  onChange={handleTypeFilterChange}
                  displayEmpty
                  size="small"
                  sx={{ minWidth: 120, background: "#fff" }}
                >
                  <MenuItem value="">All Types</MenuItem>
                  <MenuItem value="supplier">Supplier</MenuItem>
                  <MenuItem value="decorator">Decorator</MenuItem>
                </Select>
              )}
            </Box>
            {/* Clear Button */}
            {hasClear && (
              <button
                type="button"
                onClick={handleClearFilters}
                style={{
                  padding: "6px 16px",
                  background: colorPalette.colorLightGray,
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                  fontSize: "14px",
                  marginBottom: "16px",
                }}
              >
                Clear
              </button>
            )}
          </Box>
          <TableContainer
            sx={{
              border: `1px solid ${colorPalette.colorLightGray}`,
              borderRadius: "4px",
              overflow: resizableColumns ? "auto" : "hidden",
            }}
          >
            <Table
              sx={{
                minWidth: tableMinWidth,
                tableLayout: "fixed",
                display: "table",
              }}
              aria-labelledby="tableTitle"
            >
              <EnhancedTableHead
                headers={visibleHeaders}
                numSelected={selected.length}
                order={order}
                orderBy={orderBy}
                onSelectAllClick={handleSelectAllClick}
                onRequestSort={handleRequestSort}
                rowCount={rows.length}
                setSorted={setSorted}
                enableDragAndDrop={enableDragAndDrop}
                enableManualSequence={enableManualSequence}
                resizableColumns={resizableColumns}
                columnWidths={columnWidths}
                onColumnResizeStart={handleColumnResizeStart}
              />
              <TableBody>
                {loading ? (
                  <TableRow
                    sx={{
                      display: "table",
                      margin: "auto",
                    }}
                  >
                    <TableCell
                      colSpan={
                        visibleHeaders.length +
                        1 +
                        (enableDragAndDrop ? 1 : 0) +
                        (enableManualSequence ? 1 : 0)
                      }
                      align="center"
                    >
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                ) : sortedRows.length === 0 ? (
                  <TableRow
                    sx={{
                      display: "table",
                    }}
                  >
                    <TableCell
                      colSpan={
                        visibleHeaders.length +
                        1 +
                        (enableDragAndDrop ? 1 : 0) +
                        (enableManualSequence ? 1 : 0)
                      }
                      align="center"
                    >
                      No data available.
                    </TableCell>
                  </TableRow>
                ) : (
                  currentRows.map((row: any, index: number) => {
                    const isItemSelected = isSelected?.(row.id); // optional selection
                    const labelId = `enhanced-table-checkbox-${index}`;
                    const isDragging = draggedIndex === index;

                    return (
                      <TableRow
                        key={row.id || index}
                        hover
                        role="checkbox"
                        aria-checked={!!isItemSelected}
                        selected={!!isItemSelected}
                        tabIndex={-1}
                        draggable={enableDragAndDrop}
                        onDragStart={
                          enableDragAndDrop
                            ? (e) => handleDragStart(e, index)
                            : undefined
                        }
                        onDragOver={
                          enableDragAndDrop ? handleDragOver : undefined
                        }
                        onDrop={
                          enableDragAndDrop
                            ? (e) => handleDrop(e, index)
                            : undefined
                        }
                        onDragEnd={
                          enableDragAndDrop ? handleDragEnd : undefined
                        }
                        onClick={(event: any) => handleClick(event, row.id)}
                        sx={{
                          display: "table",
                          width: "100%",
                          tableLayout: "fixed",
                          paddingTop: "2px",
                          paddingBottom: "2px",
                          opacity: isDragging ? 0.5 : 1,
                          cursor: enableDragAndDrop ? "move" : "default",
                          "&:hover": {
                            backgroundColor: enableDragAndDrop
                              ? colorPalette.colorOffWhite
                              : undefined,
                          },
                          "& td": {
                            paddingTop: "10px",
                            paddingBottom: "10px",
                            // width: "18px"
                          },
                        }}
                      >
                        {enableDragAndDrop && (
                          <TableCell padding="checkbox" sx={{ width: "18px" }}>
                            <IconButton
                              size="small"
                              sx={{
                                cursor: "grab",
                                "&:active": {
                                  cursor: "grabbing",
                                },
                              }}
                              onMouseDown={(e) => e.stopPropagation()}
                            >
                              <DragIndicatorIcon />
                            </IconButton>
                          </TableCell>
                        )}
                        {enableManualSequence && (
                          <SequenceEditorCell
                            currentSequence={row.sequence}
                            upperBound={
                              sequenceUpperBound ?? sortedRows.length
                            }
                            onCommit={(next) =>
                              onSequenceChange?.(row.id, next)
                            }
                            ariaLabel={`sequence for ${
                              row.product?.text || row.id
                            }`}
                          />
                        )}

                        <TableCell padding="checkbox" sx={{ width: "18px" }}>
                          <Checkbox
                            color="primary"
                            checked={!!isItemSelected}
                            inputProps={{
                              "aria-labelledby": labelId,
                            }}
                          />
                        </TableCell>
                        {visibleHeaders.map((header: any) => {
                          let value = row[header.id];
                          if (header.type === "array" && Array.isArray(value)) {
                            value = value.join(", ");
                          } else if (
                            header.type === "number" &&
                            typeof value === "number"
                          ) {
                            value = value.toLocaleString()?.replace(",", "");
                          } else if (header.type === "date") {
                            value = value.length
                              ? new Date(value).toLocaleDateString()
                              : "";
                          }
                          return (
                            <TableCell
                              key={header.id}
                              align={
                                header.centerAlign
                                  ? "center"
                                  : header.numeric
                                  ? "right"
                                  : "left"
                              }
                              padding={
                                header.disablePadding ? "none" : "normal"
                              }
                              sortDirection={
                                orderBy === header.id ? (order as any) : false
                              }
                              sx={getBodyColumnSx(header)}
                            >
                              {header.id === "action" ? (
                                value ? (
                                  <IconButton
                                    onClick={(e: any) =>
                                      handlePopoverOpen(e, row)
                                    }
                                  >
                                    <MoreVertIcon />
                                  </IconButton>
                                ) : null
                              ) : header.id === "product" ||
                                header.id === "collectionName" ? (
                                typeof value === "string" ? (
                                  header.id === "collectionName" ? (
                                    <Link
                                      onClick={(e) => e.stopPropagation()}
                                      to={appRoutes.MARKETPLACE_COLLECTION_DETAILS(
                                        row?.id
                                      )}
                                      style={{
                                        color:
                                          colorPalette.colorVibrantMediumBlue,
                                        textDecoration: "none",
                                        marginRight: 8,
                                      }}
                                    >
                                      {value}
                                    </Link>
                                  ) : (
                                    value
                                  )
                                ) : typeof value === "object" &&
                                  value !== null ? (
                                  <Box
                                    display="flex"
                                    alignItems="center"
                                    gap={1}
                                  >
                                    {value.image && (
                                      <img
                                        src={value.image}
                                        alt={value.text || "product"}
                                        width={32}
                                        height={32}
                                        style={tableStyle.imageStyle}
                                      />
                                    )}
                                    <div style={{ display: "grid" }}>
                                      <Box sx={tableStyle.cellStyle}>
                                        <Link
                                          onClick={(e) => e.stopPropagation()}
                                          to={
                                            header.source === "catalog"
                                              ? appRoutes.MARKETPLACE_CATALOG_DETAILS(
                                                  value.id
                                                )
                                              : appRoutes.VENDOR_PRODUCT_DETAILS(
                                                  value.id
                                                )
                                          }
                                          style={{
                                            color:
                                              colorPalette.colorVibrantMediumBlue,
                                            textDecoration: "none",
                                            marginRight: 8,
                                          }}
                                        >
                                          {value.text}
                                        </Link>
                                      </Box>
                                      {value.color && (
                                        <Typography
                                          sx={{
                                            ...tableStyle.dataTextSmall,
                                            fontSize: "12px",
                                          }}
                                        >
                                          Color: {value.color.colorName}{" "}
                                          <div
                                            style={{
                                              marginLeft: 10,
                                              width: 12,
                                              height: 12,
                                              borderRadius: "50%",
                                              background: value.color.colorCode,
                                              border: `0.5px solid ${colorPalette.colorGrayishBlue}`,
                                            }}
                                          ></div>
                                        </Typography>
                                      )}
                                    </div>
                                  </Box>
                                ) : null
                              ) : (
                                <div
                                  style={
                                    header.id === "status"
                                      ? getStatusBadgeStyle(String(value ?? ""))
                                      : {}
                                  }
                                >
                                  {header.id === "vendor" ? (
                                    <Link
                                      onClick={(e) => e.stopPropagation()}
                                      to={`${appRoutes.VENDOR_DETAILS(
                                        value?.id
                                      )}?tabIndex=0`}
                                      style={{
                                        color:
                                          colorPalette.colorVibrantMediumBlue,
                                        textDecoration: "none",
                                        marginRight: 8,
                                      }}
                                    >
                                      {value?.name}
                                    </Link>
                                  ) : header.id === "service" ? (
                                    <Link
                                      onClick={(e) => e.stopPropagation()}
                                      to={`${appRoutes.VENDOR_DETAILS(
                                        row?.vendor?.id
                                      )}?tabIndex=2`}
                                      style={{
                                        color:
                                          colorPalette.colorVibrantMediumBlue,
                                        textDecoration: "none",
                                        marginRight: 8,
                                      }}
                                    >
                                      {capitalizeFirstLetter(value)}
                                    </Link>
                                  ) : (
                                    <Box
                                      sx={{
                                        ...tableStyle.cellStyle,
                                        ...(header.wrapText
                                          ? {
                                              whiteSpace: "normal",
                                              textOverflow: "clip",
                                              overflow: "visible",
                                              maxWidth: "100%",
                                            }
                                          : {}),
                                      }}
                                    >
                                      <Typography
                                        sx={{
                                          ...tableStyle.dataTextRegular,
                                          ...(header.wrapText
                                            ? {
                                                whiteSpace: "normal",
                                                wordBreak: "break-word",
                                                overflowWrap: "anywhere",
                                              }
                                            : {}),
                                        }}
                                      >
                                        {header.id === "price" ||
                                        typeof value === "number"
                                          ? `$${Number(value).toFixed(2)}`
                                          : header.id === "tags"
                                          ? value
                                          : capitalizeFirstLetter(value)}
                                      </Typography>
                                    </Box>
                                  )}
                                </div>
                              )}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
            <ActionPopover
              anchorEl={anchorEl}
              onClose={handlePopoverClose}
              rowData={popoverRow}
              actions={actions || []}
              onActionClick={handleActionClick}
            />
          </TableContainer>
          <TablePagination
            sx={{ display: "block" }}
            rowsPerPageOptions={[]}
            count={totalCount || 0}
            rowsPerPage={rowsPerPage || 0}
            page={page || 0}
            {...(onPageChange
              ? { onPageChange: onPageChange }
              : { onPageChange: () => {} })}
          />
          {showSnackbar && selected.length > 0 && (
            <Snacker
              openToast={openSnackerToast}
              handleCloseToast={() => setOpenSnackerToast(false)}
              selectedIds={selected}
              snackbarText={snackbarText}
              action={snackbarActions}
            />
          )}
        </Paper>
      </Box>
    </Grid>
  );
}
export default GenericTable;
